/**
 * Utility functions for bounds operations
 */

import type { ApiBounds } from "../shared/interfaces";

const FULL_PAGE_BOUNDS_TOLERANCE = 0.02;

export function isFullPageBounds(
    bounds: ApiBounds,
    tolerance = FULL_PAGE_BOUNDS_TOLERANCE,
): boolean {
    return (
        bounds.xMin <= tolerance &&
        bounds.yMin <= tolerance &&
        bounds.xMax >= 1 - tolerance &&
        bounds.yMax >= 1 - tolerance
    );
}

/** True when two bounds have a positive-area intersection (edge-touching is not overlap). */
export function boundsOverlap(a: ApiBounds, b: ApiBounds): boolean {
    return a.xMin < b.xMax && a.xMax > b.xMin && a.yMin < b.yMax && a.yMax > b.yMin;
}

/**
 * Calculate the union (bounding box) of multiple bounds
 * @param bounds Array of bounds to compute union for
 * @returns Aggregated bounds that encompass all input bounds
 */
export function unionBounds(bounds: ApiBounds[]): {
    xMin: number;
    yMin: number;
    xMax: number;
    yMax: number;
} {
    if (bounds.length === 0) {
        throw new Error("Cannot compute union bounds of empty array");
    }

    return bounds.reduce(
        (acc, b) => ({
            xMin: Math.min(acc.xMin, b.xMin),
            yMin: Math.min(acc.yMin, b.yMin),
            xMax: Math.max(acc.xMax, b.xMax),
            yMax: Math.max(acc.yMax, b.yMax),
        }),
        {
            xMin: Number.MAX_VALUE,
            yMin: Number.MAX_VALUE,
            xMax: Number.MIN_VALUE,
            yMax: Number.MIN_VALUE,
        },
    );
}
